import { useState } from "react";
import { trpc } from "../hooks/trpc";
import { Smartphone, Trash2, ShieldOff, AlertCircle, KeyRound, CheckCircle2 } from "lucide-react";

function formatDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function daysBetween(a: Date, b: Date): number {
  return Math.floor((b.getTime() - a.getTime()) / 86_400_000);
}

export function TrustedDevicesSection() {
  const utils = trpc.useUtils();
  const { data, isLoading, error } = trpc.trustedDevices.list.useQuery();
  const revokeMutation = trpc.trustedDevices.revoke.useMutation({
    onSuccess: () => utils.trustedDevices.list.invalidate(),
  });
  const revokeAllMutation = trpc.trustedDevices.revokeAll.useMutation({
    onSuccess: () => utils.trustedDevices.list.invalidate(),
  });

  // MFA-Modal-State: enthaelt entweder { kind: "single", deviceId } oder { kind: "all" }.
  const [pending, setPending] = useState<
    | { kind: "single"; deviceId: number; deviceName: string | null }
    | { kind: "all" }
    | null
  >(null);
  const [mfaCode, setMfaCode] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  if (isLoading) {
    return (
      <section className="card p-5">
        <div className="text-sm" style={{ color: "var(--color-muted)" }}>Lade Geraete…</div>
      </section>
    );
  }

  if (error) {
    const forbidden = error.data?.code === "FORBIDDEN";
    return (
      <section className="card p-5">
        <div className="flex items-center gap-2 text-sm" style={{ color: forbidden ? "var(--color-muted-foreground)" : "var(--color-danger)" }}>
          <AlertCircle size={16} />
          {forbidden
            ? "Für dieses Konto stehen derzeit keine verwaltbaren vertrauenswürdigen Geräte zur Verfügung."
            : error.message}
        </div>
      </section>
    );
  }

  const devices = data?.devices ?? [];
  const trustedDays = data?.trustedDeviceDays ?? 90;
  const now = Date.now();
  const isActive = (device: typeof devices[number]) => !device.revoked && new Date(device.expiresAt).getTime() > now;
  const activeDevices = devices.filter(isActive);
  const visibleDevices = showHistory ? devices : activeDevices;

  const submitMfaAction = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionError(null);
    try {
      if (pending?.kind === "single") {
        await revokeMutation.mutateAsync({ deviceId: pending.deviceId, mfaCode: mfaCode || undefined });
      } else if (pending?.kind === "all") {
        await revokeAllMutation.mutateAsync({ mfaCode: mfaCode || undefined });
      }
      setPending(null);
      setMfaCode("");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Aktion fehlgeschlagen";
      setActionError(msg);
    }
  };

  const startRevoke = (deviceId: number, deviceName: string | null) => {
    setActionError(null);
    setMfaCode("");
    setPending({ kind: "single", deviceId, deviceName });
  };
  const startRevokeAll = () => {
    setActionError(null);
    setMfaCode("");
    setPending({ kind: "all" });
  };

  return (
    <>
      <section className="card p-5">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ fontFamily: "var(--font-display)" }}>
            Vertrauenswuerdige Geraete
          </h2>
          <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: "var(--color-muted)" }}>
            {trustedDays} Tage Frist
          </span>
        </div>

        <p className="text-[12px] mb-4" style={{ color: "var(--color-muted-foreground)" }}>
          Auf diesen Geraeten ueberspringt der Login den 2FA-Schritt. Passwort bleibt jedes Mal Pflicht.
          Bei Passwort-Aenderung oder MFA-Wechsel werden alle Geraete automatisch entzogen.
        </p>

        <label className="flex items-center gap-2 text-[11px] mb-3 cursor-pointer" style={{ color: "var(--color-muted)" }}>
          <input
            type="checkbox"
            checked={showHistory}
            onChange={(event) => setShowHistory(event.target.checked)}
          />
          Gerätehistorie mit abgelaufenen und entzogenen Geräten anzeigen
        </label>

        {visibleDevices.length === 0 ? (
          <div className="text-sm py-4" style={{ color: "var(--color-muted)" }}>
            {showHistory
              ? "Keine Geräte registriert. Beim nächsten Login mit 2FA wird dieses Gerät eingetragen."
              : "Keine aktiven Geräte. Abgelaufene oder entzogene Geräte können über die Gerätehistorie eingeblendet werden."}
          </div>
        ) : (
          <div className="flex flex-col divide-y" style={{ borderColor: "var(--color-border)" }}>
            {visibleDevices.map(device => {
              const expiresInDays = daysBetween(new Date(), new Date(device.expiresAt));
              return (
                <div key={device.id} className="py-3 flex items-center gap-3">
                  <div
                    className="w-10 h-10 flex items-center justify-center flex-shrink-0"
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "var(--radius-sm)",
                      color: device.current ? "var(--color-primary)" : "var(--color-foreground)",
                    }}
                  >
                    <Smartphone size={18} strokeWidth={1.6} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold" style={{ color: "var(--color-foreground)" }}>
                        {device.name ?? "Unbenanntes Geraet"}
                      </span>
                      {device.current && (
                        <span
                          className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ background: "rgba(0,163,224,0.12)", color: "var(--color-primary)" }}
                        >
                          dieses Geraet
                        </span>
                      )}
                      {device.revoked && (
                        <span
                          className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ background: "rgba(224, 97, 90, 0.12)", color: "var(--color-danger)" }}
                        >
                          entzogen
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] font-mono mt-1" style={{ color: "var(--color-muted)" }}>
                      Erstellt: {formatDate(device.createdAt)} · Zuletzt: {formatDate(device.lastUsedAt)}
                      {!device.revoked && expiresInDays > 0 && (
                        <> · laeuft in {expiresInDays} Tagen ab</>
                      )}
                      {!device.revoked && expiresInDays <= 0 && (
                        <> · abgelaufen</>
                      )}
                    </div>
                  </div>
                  {isActive(device) && (
                    <button
                      type="button"
                      onClick={() => startRevoke(device.id, device.name)}
                      className="btn btn-secondary text-xs px-3 py-1.5"
                      title="Vertrauen fuer dieses Geraet entziehen"
                      disabled={revokeMutation.isPending}
                    >
                      <Trash2 size={12} /> Entziehen
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {activeDevices.length > 0 && (
          <div className="mt-4 pt-4 border-t flex items-center justify-between" style={{ borderColor: "var(--color-border)" }}>
            <p className="text-[11px]" style={{ color: "var(--color-muted)" }}>
              Auf allen Geraeten abmelden invalidiert zusaetzlich alle aktiven Sessions.
            </p>
            <button
              type="button"
              onClick={startRevokeAll}
              className="btn btn-danger text-xs px-3 py-1.5"
              disabled={revokeAllMutation.isPending}
            >
              <ShieldOff size={12} /> Alle Geraete abmelden
            </button>
          </div>
        )}
      </section>

      {pending && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.45)" }}
          onClick={() => setPending(null)}
        >
          <form
            onClick={e => e.stopPropagation()}
            onSubmit={submitMfaAction}
            className="card p-6 w-full max-w-sm space-y-4"
          >
            <div className="flex items-center gap-2">
              <KeyRound size={18} style={{ color: "var(--color-primary)" }} />
              <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ fontFamily: "var(--font-display)" }}>
                Bestaetigen
              </h3>
            </div>
            <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
              {pending.kind === "single"
                ? <>Geraet <strong>{pending.deviceName ?? "ohne Namen"}</strong> als nicht mehr vertrauenswuerdig markieren?</>
                : <>Alle Geraete entziehen und Sessions schliessen?</>}
            </p>
            <input
              type="text"
              value={mfaCode}
              onChange={e => setMfaCode(e.target.value)}
              className="input text-center text-lg tracking-[0.5em] font-mono"
              maxLength={6}
              placeholder="000000"
              autoFocus
              inputMode="numeric"
            />
            <p className="text-[10px]" style={{ color: "var(--color-muted)" }}>
              MFA-Code aus deiner Authenticator-App. Leer lassen, falls 2FA nicht aktiv ist.
            </p>
            {actionError && (
              <div className="flex items-center gap-2 text-xs" style={{ color: "var(--color-danger)" }}>
                <AlertCircle size={14} /> {actionError}
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button type="button" className="btn btn-secondary text-xs px-3 py-1.5"
                onClick={() => setPending(null)}>
                Abbrechen
              </button>
              <button type="submit"
                className="btn btn-primary text-xs px-3 py-1.5"
                disabled={revokeMutation.isPending || revokeAllMutation.isPending}>
                <CheckCircle2 size={12} /> Bestaetigen
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
